import React from "react";
import { Link } from "react-router-dom";
import logo from "../images/cape.png";
import heroVideo from "./images/uber.mp4";
import workerVideo from "./images/worker.mp4";

function CaregiverMarketingPage() {
  return (
    <div style={page}>
      <style>{responsiveStyles}</style>

      <div style={headerWrap}>
        <header style={headerShell} className="marketing-header-shell">
          <Link to="/" style={brandLink} aria-label="Return to home page">
            <div style={logoWrap}>
              <img src={logo} alt="UberCARE logo" style={logoStyle} />
            </div>
          </Link>
        </header>
      </div>

      <section style={heroShell}>
        <div style={heroLayout} className="marketing-hero-layout">
          <div>
            <div style={badge}>Welcome to UberCARE</div>
            <h1 style={heroTitle}>
              A warm, professional place for caregivers to be seen.
            </h1>
            <p style={heroSubtitle}>
              UberCARE gives caregivers a thoughtful place to introduce their
              services, complete a profile with confidence, and appear on the
              map in a way that helps families discover and understand the
              support they offer.
            </p>

            <div style={signalRow} className="marketing-signal-row">
              <SignalPill text="A welcoming beginning" />
              <SignalPill text="A complete profile" />
              <SignalPill text="A visible map presence" />
            </div>

            <div style={infoGrid} className="marketing-info-grid">
              <InfoCard
                title="What caregivers can expect"
                text="Caregivers can move from account creation into profile setup and then into a dashboard experience where their details, status, and map tools all belong together."
              />
              <InfoCard
                title="Why the map matters"
                text="Appearing on the map gives families a clearer way to discover caregivers, review important details, and feel ready to reach out when support is needed."
              />
            </div>

            <div style={supportStrip}>
              <div style={supportStripLabel}>A gentle introduction</div>
              <p style={cardText}>
                This page is designed for marketing, so it keeps the focus on
                the caregiver journey, the value of profile setup, and the
                confidence that comes from being visible to families.
              </p>
            </div>
          </div>

          <div style={heroPanel}>
            <div style={videoGrid} className="marketing-video-grid">
              <VideoCard
                title="Set your own hourly rate"
                text="UberCARE allows caregivers to present an hourly rate as part of their profile, giving families an early sense of what to expect."
                videoSrc={heroVideo}
                note="Caregiver experience"
              />

              <VideoCard
                title="Appear on the map"
                text="A completed caregiver profile can appear on the map, helping families discover where support may be available and making the first point of connection feel more natural."
                videoSrc={workerVideo}
                note="Map presence"
              />
            </div>
          </div>
        </div>
      </section>

      <section style={section}>
        <div style={sectionInner}>
          <div style={sectionHeader}>
            <p style={eyebrow}>Caregiver journey</p>
            <h2 style={sectionTitle}>
              Three simple steps that lead to visibility
            </h2>
            <p style={sectionSubtitle}>
              From joining UberCARE to being discovered on the map, the
              experience is designed to feel welcoming and clear.
            </p>
          </div>

          <div style={journeyGrid} className="marketing-journey-grid">
            <StepCard
              number="1"
              stepLabel="Step 01"
              title="Create an account"
              text="Caregivers can begin by joining UberCARE and opening the door to a fuller professional presence."
            />
            <StepCard
              number="2"
              stepLabel="Step 02"
              title="Complete the profile"
              text="Adding contact details, location, payment information, and hourly rate helps families learn more with confidence."
            />
            <StepCard
              number="3"
              stepLabel="Step 03"
              title="Be seen on the map"
              text="Once the profile is ready, caregivers can appear on the map and become easier for families to discover and contact."
            />
          </div>
        </div>
      </section>

      <section style={supportSection}>
        <div style={supportLayout} className="marketing-support-layout">
          <div style={supportCopyCard}>
            <p style={supportEyebrow}>Why this matters</p>
            <h2 style={supportTitle}>
              A profile that feels trustworthy before the first message.
            </h2>
            <p style={supportText}>
              UberCARE gives caregivers more than a simple listing. It creates
              room for families to understand the caregiver&apos;s services,
              location, rate, and availability before deciding to connect.
            </p>

            <div style={benefitList}>
              <BenefitRow
                icon="M"
                title="Map visibility"
                text="Being visible on the map helps a caregiver feel present in the network rather than hidden behind forms alone."
              />
              <BenefitRow
                icon="P"
                title="Profile confidence"
                text="A fuller profile gives families a stronger sense of who the caregiver is, what support is offered, and how to begin."
              />
            </div>
          </div>

          <div style={supportAsideCard}>
            <div style={badge}>Marketing message</div>
            <h3 style={asideTitle}>
              A thoughtful introduction can go a long way.
            </h3>
            <p style={asideText}>
              This standalone page is shaped for marketing use, with a
              welcoming tone, persuasive caregiver-focused messaging, and the
              same videos shown on the caregiver application home page.
            </p>
          </div>
        </div>
      </section>

      <section style={ctaSection}>
        <div style={ctaCard}>
          <div>
            <div style={badge}>Ready to share</div>
            <h2 style={ctaTitle}>
              A marketing home page focused on caregivers, trust, and discovery.
            </h2>
            <p style={ctaText}>
              This version keeps the home-page atmosphere while removing links
              and login actions, making it a cleaner standalone page for
              presenting the caregiver experience.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function SignalPill({ text }) {
  return <div style={signalPill}>{text}</div>;
}

function InfoCard({ title, text }) {
  return (
    <div style={infoCard}>
      <h3 style={infoTitle}>{title}</h3>
      <p style={cardText}>{text}</p>
    </div>
  );
}

function VideoCard({ title, text, videoSrc, note }) {
  return (
    <div style={videoCard}>
      <div style={mediaHeader}>
        <span style={mediaTag}>Preview</span>
        <span style={mediaNote}>{note}</span>
      </div>
      <div style={videoFrame}>
        <video
          style={videoStyle}
          controls
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      </div>
      <div style={videoCopy}>
        <h3 style={videoTitle}>{title}</h3>
        <p style={cardText}>{text}</p>
      </div>
    </div>
  );
}

function StepCard({ number, stepLabel, title, text }) {
  return (
    <div style={stepCard}>
      <div style={stepIcon}>{number}</div>
      <div style={stepNumber}>{stepLabel}</div>
      <h3 style={stepTitle}>{title}</h3>
      <p style={cardText}>{text}</p>
    </div>
  );
}

function BenefitRow({ icon, title, text }) {
  return (
    <div style={benefitRow}>
      <div style={benefitIcon}>{icon}</div>
      <div>
        <h3 style={benefitTitle}>{title}</h3>
        <p style={benefitText}>{text}</p>
      </div>
    </div>
  );
}

const responsiveStyles = `
  .marketing-hero-layout {
    grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
  }

  .marketing-info-grid,
  .marketing-video-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .marketing-journey-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .marketing-support-layout {
    grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  }

  @media (max-width: 1040px) {
    .marketing-hero-layout,
    .marketing-info-grid,
    .marketing-video-grid,
    .marketing-support-layout {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 820px) {
    .marketing-journey-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 760px) {
    .marketing-header-shell {
      padding: 10px 14px !important;
    }

    .marketing-hero-layout,
    .marketing-info-grid,
    .marketing-video-grid,
    .marketing-journey-grid,
    .marketing-support-layout {
      gap: 14px !important;
    }

    .marketing-signal-row > * {
      width: 100% !important;
      justify-content: center !important;
    }
  }

  @media (max-width: 420px) {
    .marketing-header-shell {
      border-radius: 18px !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #eef4f7 0%, #e7eef2 100%)",
  color: "#123a59"
};

const headerWrap = {
  position: "sticky",
  top: "10px",
  zIndex: 20,
  padding: "10px 14px 0"
};

const headerShell = {
  maxWidth: "1320px",
  margin: "0 auto",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 16px",
  borderRadius: "24px",
  background: "rgba(12, 64, 98, 0.94)",
  border: "1px solid rgba(167, 215, 233, 0.2)",
  boxShadow: "0 20px 42px rgba(8, 34, 55, 0.16)",
  backdropFilter: "blur(18px)"
};

const brandLink = {
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none"
};

const logoWrap = {
  width: "92px",
  height: "92px",
  borderRadius: "16px",
  background: "linear-gradient(135deg, #114f77 0%, #1d6b8f 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};

const logoStyle = {
  width: "64px",
  height: "64px",
  objectFit: "contain"
};

const heroShell = {
  position: "relative",
  overflow: "hidden",
  padding: "22px 22px 28px",
  background:
    "radial-gradient(circle at 18% 16%, rgba(64, 155, 145, 0.12), transparent 22%), radial-gradient(circle at 84% 20%, rgba(29, 106, 143, 0.1), transparent 20%), linear-gradient(180deg, #f7fbfc 0%, #eef5f7 100%)"
};

const heroLayout = {
  maxWidth: "1320px",
  margin: "0 auto",
  display: "grid",
  gap: "28px",
  alignItems: "start"
};

const badge = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 14px",
  borderRadius: "999px",
  background: "rgba(47, 127, 117, 0.12)",
  border: "1px solid rgba(47, 127, 117, 0.16)",
  color: "#2a6d66",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const heroTitle = {
  margin: "18px 0 0",
  fontSize: "clamp(2.3rem, 4.7vw, 4.1rem)",
  lineHeight: 1.02,
  letterSpacing: 0,
  color: "#164f73"
};

const heroSubtitle = {
  margin: "16px 0 0",
  maxWidth: "720px",
  fontSize: "clamp(1rem, 1.8vw, 1.22rem)",
  lineHeight: 1.7,
  color: "#58718a"
};

const signalRow = {
  marginTop: "18px",
  display: "flex",
  flexWrap: "wrap",
  gap: "12px"
};

const signalPill = {
  padding: "10px 14px",
  borderRadius: "999px",
  background: "rgba(255, 255, 255, 0.88)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  color: "#4e697f",
  fontSize: "13px",
  fontWeight: "700"
};

const infoGrid = {
  marginTop: "18px",
  display: "grid",
  gap: "14px"
};

const infoCard = {
  padding: "18px",
  borderRadius: "20px",
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 12px 26px rgba(12, 42, 68, 0.05)"
};

const infoTitle = {
  margin: "0 0 10px",
  color: "#164f73"
};

const cardText = {
  margin: 0,
  color: "#58718a",
  lineHeight: 1.7
};

const supportStrip = {
  marginTop: "16px",
  padding: "18px 20px",
  borderRadius: "22px",
  background: "linear-gradient(135deg, rgba(20, 90, 131, 0.08), rgba(58, 166, 151, 0.12))",
  border: "1px solid rgba(20, 90, 131, 0.08)"
};

const supportStripLabel = {
  marginBottom: "8px",
  color: "#2f7f75",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const heroPanel = {
  display: "grid",
  gap: "16px"
};

const videoGrid = {
  marginTop: "18px",
  display: "grid",
  gap: "14px"
};

const videoCard = {
  padding: "14px",
  borderRadius: "24px",
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 18px 38px rgba(12, 42, 68, 0.08)"
};

const mediaHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginBottom: "12px"
};

const mediaTag = {
  display: "inline-flex",
  padding: "6px 10px",
  borderRadius: "999px",
  background: "rgba(47, 127, 117, 0.12)",
  color: "#2f7f75",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em"
};

const mediaNote = {
  color: "#6a8194",
  fontSize: "12px",
  fontWeight: "700"
};

const videoFrame = {
  borderRadius: "22px",
  overflow: "hidden",
  background: "#dce8ef",
  border: "1px solid rgba(20, 90, 131, 0.08)"
};

const videoStyle = {
  width: "100%",
  display: "block",
  aspectRatio: "10 / 13",
  objectFit: "cover",
  background: "#dce8ef"
};

const videoCopy = {
  marginTop: "12px"
};

const videoTitle = {
  margin: "0 0 10px",
  color: "#164f73"
};

const section = {
  padding: "36px 22px",
  background: "linear-gradient(180deg, #f2f7fa 0%, #edf3f6 100%)"
};

const sectionInner = {
  maxWidth: "1320px",
  margin: "0 auto"
};

const sectionHeader = {
  maxWidth: "760px",
  margin: "0 auto 30px",
  textAlign: "center"
};

const eyebrow = {
  margin: "0 0 12px",
  color: "#2f7f75",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const sectionTitle = {
  margin: 0,
  fontSize: "clamp(1.75rem, 2.7vw, 2.35rem)",
  lineHeight: 1.12,
  color: "#164f73"
};

const sectionSubtitle = {
  margin: "14px 0 0",
  color: "#58718a",
  lineHeight: 1.7
};

const journeyGrid = {
  display: "grid",
  gap: "18px"
};

const stepCard = {
  padding: "18px",
  borderRadius: "20px",
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 12px 26px rgba(12, 42, 68, 0.05)"
};

const stepIcon = {
  width: "50px",
  height: "50px",
  borderRadius: "16px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(58, 166, 151, 0.12)",
  color: "#2f7f75",
  fontSize: "22px",
  marginBottom: "14px",
  fontWeight: "800"
};

const stepNumber = {
  fontSize: "11px",
  fontWeight: "800",
  color: "#5f8aa8",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: "10px"
};

const stepTitle = {
  margin: "0 0 10px",
  color: "#164f73"
};

const supportSection = {
  padding: "0 22px 36px",
  background: "linear-gradient(180deg, #edf3f6 0%, #e6eef2 100%)"
};

const supportLayout = {
  maxWidth: "1320px",
  margin: "0 auto",
  display: "grid",
  gap: "18px"
};

const supportCopyCard = {
  padding: "28px",
  borderRadius: "28px",
  background: "linear-gradient(180deg, #164f73 0%, #1c6284 100%)",
  color: "#ffffff",
  boxShadow: "0 18px 38px rgba(12, 42, 68, 0.08)"
};

const supportEyebrow = {
  margin: "0 0 12px",
  color: "#86d1c7",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const supportTitle = {
  margin: "0 0 12px",
  color: "#ffffff",
  fontSize: "clamp(1.75rem, 2.8vw, 2.2rem)",
  lineHeight: 1.15
};

const supportText = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.82)",
  lineHeight: 1.7
};

const benefitList = {
  display: "grid",
  gap: "14px",
  marginTop: "22px"
};

const benefitRow = {
  display: "grid",
  gridTemplateColumns: "54px minmax(0, 1fr)",
  gap: "14px",
  alignItems: "start",
  padding: "16px",
  borderRadius: "20px",
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.1)"
};

const benefitIcon = {
  width: "54px",
  height: "54px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(58, 166, 151, 0.16)",
  color: "#9ce5da",
  fontSize: "20px",
  fontWeight: "800"
};

const benefitTitle = {
  margin: "0 0 6px",
  color: "#ffffff",
  fontSize: "17px"
};

const benefitText = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.82)",
  lineHeight: 1.7
};

const supportAsideCard = {
  padding: "26px",
  borderRadius: "28px",
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 12px 26px rgba(12, 42, 68, 0.05)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center"
};

const asideTitle = {
  margin: "16px 0 12px",
  fontSize: "24px",
  lineHeight: 1.3,
  color: "#164f73"
};

const asideText = {
  margin: 0,
  color: "#58718a",
  lineHeight: 1.7
};

const ctaSection = {
  padding: "0 22px 42px",
  background: "linear-gradient(180deg, #e6eef2 0%, #e2ebf0 100%)"
};

const ctaCard = {
  maxWidth: "1320px",
  margin: "0 auto",
  padding: "24px",
  borderRadius: "30px",
  background: "linear-gradient(135deg, #f9fcfd 0%, #eef5f7 100%)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 18px 34px rgba(12, 42, 68, 0.08)",
  display: "grid",
  gap: "24px",
  alignItems: "center"
};

const ctaTitle = {
  margin: "16px 0 12px",
  fontSize: "clamp(1.8rem, 2.9vw, 2.3rem)",
  lineHeight: 1.12,
  color: "#164f73"
};

const ctaText = {
  margin: 0,
  color: "#58718a",
  lineHeight: 1.7
};

export default CaregiverMarketingPage;
